/**
 * Favorite league ids for the create-challenge games step (stored in User.favoriteLeagueIds).
 * Reads from UserProfileContext (single GET profile) so no duplicate profile request.
 * Exposes isLoading so CreateGamesStep can wait before sorting to avoid FOUSC.
 */

import { useMemo, useCallback } from 'react';
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext';
import { useUserProfile } from '../../shared/contexts/UserProfileContext';
import { useApiClient } from '../../../helpers/apiClient.helper';
import {
  updateFavoriteLeagueIds,
  type ApiClientFn,
} from '../helpers/favoriteLeaguesApi';

export interface UseFavoriteLeaguesReturn {
  /** Set of league ids (from DB). Empty when not signed in or until load completes. */
  favoriteLeagueIds: Set<string>;
  /** True until first load from API has finished (or when no userId). */
  isLoading: boolean;
  /** Toggle a league; updates state and persists via PUT /users/profile. */
  toggleFavorite: (leagueId: string) => Promise<void>;
}

export function useFavoriteLeagues(): UseFavoriteLeaguesReturn {
  const { userId } = useSupabaseAuth();
  const { profile, isLoading: isLoadingProfile, updateProfile } = useUserProfile();
  const apiClient = useApiClient();

  const favoriteLeagueIds = useMemo(
    () => new Set(profile?.favoriteLeagueIds ?? []),
    [profile?.favoriteLeagueIds]
  );
  const isLoading = !!userId && isLoadingProfile;

  const toggleFavorite = useCallback(
    async (leagueId: string) => {
      if (!userId) return;
      const fn = apiClient as ApiClientFn;
      const next = new Set(favoriteLeagueIds);
      if (next.has(leagueId)) next.delete(leagueId);
      else next.add(leagueId);
      const nextIds = Array.from(next);
      updateProfile({ favoriteLeagueIds: nextIds });
      const updated = await updateFavoriteLeagueIds(nextIds, fn);
      updateProfile({ favoriteLeagueIds: updated });
    },
    [userId, apiClient, favoriteLeagueIds, updateProfile]
  );

  return { favoriteLeagueIds, isLoading, toggleFavorite };
}
